import Axios, { AxiosRequestConfig } from "axios";
import {
  getLoginUrl,
  getStoredAccessToken,
  refreshToken,
  removeAllTokens,
  ROUTES,
} from "~/helpers";
import { useAuthStore } from "~/stores/authStore";
import { AUTH_API_URL, BASE_API_URL, IS_NA_INSTANCE } from "~/utils/apiUrl";

export const axiosInstance = Axios.create({ baseURL: BASE_API_URL });

// Endpoints that talk to Cognito must be sent to the main API even on the NA
// instance, which cannot reach Cognito (see AUTH_API_URL). Fixed paths:
const AUTH_API_PATHS = [
  "/login",
  "/logout",
  "/refresh-token",
  "/forgot-password",
  "/reset-password",
  "/complete-new-password",
  "/auth/passwordless/initiate",
  "/auth/passwordless/verify",
  "/auth/social/register",
  "/organizations/create",
  "/organizations/password-rules",
];

// ...and paths that carry ids. The method is part of the match because some of
// these paths also serve requests that never reach Cognito.
const AUTH_API_ROUTES: { method: string; pattern: RegExp }[] = [
  // Resolves a share token to the case details a guest needs to log in.
  { method: "GET", pattern: /^\/organizations\/share\/[^/]+$/ },
  // Creates a Cognito user for the invited member.
  { method: "POST", pattern: /^\/organizations\/[^/]+\/invite$/ },
  // Rewrites the member's Cognito email; PATCH (role change) never hits Cognito.
  { method: "DELETE", pattern: /^\/organizations\/[^/]+\/users\/[^/]+$/ },
  // Creates a guest Cognito user and returns a share link.
  {
    method: "POST",
    pattern: /^\/organizations\/[^/]+\/projects\/[^/]+\/share$/,
  },
];

const isAuthApiPath = (url?: string, method?: string) => {
  if (!url) return false;
  const path = url.split(/[?#]/)[0];
  if (AUTH_API_PATHS.includes(path)) return true;

  const requestMethod = (method ?? "GET").toUpperCase();
  return AUTH_API_ROUTES.some(
    (route) => route.method === requestMethod && route.pattern.test(path)
  );
};

// Flag to prevent multiple redirects
let isRedirecting = false;

// Paths that belong to AuthLayout where we should not perform token checks/redirects
const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/change-password",
  "/auth/callback",
  "/transcribing",
  "/share",
  "/impersonate",
];

const isAuthRoute = (pathname: string) =>
  AUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

axiosInstance.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isAuthApiPath(config.url, config.method)) {
    config.baseURL = AUTH_API_URL;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isOnAuthRoute =
      typeof location !== "undefined" && isAuthRoute(location.pathname);

    if (error?.response?.status === 451) {
      removeAllTokens();
      const { setAuthExpired, isImpersonating } = useAuthStore.getState();
      setAuthExpired(true);
      if (isImpersonating) {
        const { setImpersonating } = useAuthStore.getState();
        setImpersonating(false);
      }
      if (IS_NA_INSTANCE) {
        window.location.href = import.meta.env.VITE_APP_URL;
      } else {
        window.location.href = import.meta.env.VITE_APP_URL_NA;
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 406) {
      removeAllTokens();
      const { setAuthExpired, isImpersonating } = useAuthStore.getState();
      setAuthExpired(true);
      if (isImpersonating) {
        const { setImpersonating } = useAuthStore.getState();
        setImpersonating(false);
      }
      window.location.href = ROUTES.LOGIN;
      return Promise.reject(error);
    }
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Do not attempt refresh or redirect when on AuthLayout routes
      if (isOnAuthRoute) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // impersonation tokens have no refresh token, go straight to logout
      const { isImpersonating } = useAuthStore.getState();
      if (isImpersonating) {
        removeAllTokens();
        const { setAuthExpired, setImpersonating } = useAuthStore.getState();
        setAuthExpired(true);
        setImpersonating(false);

        if (!isRedirecting) {
          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshToken({ force: true });
        if (newAccessToken) {
          return axiosInstance(originalRequest);
        } else {
          removeAllTokens();
          const { setAuthExpired } = useAuthStore.getState();
          setAuthExpired(true);

          if (isOnAuthRoute || isRedirecting) {
            return Promise.reject(error);
          }

          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
      } catch (refreshError) {
        removeAllTokens();
        const { setAuthExpired } = useAuthStore.getState();
        setAuthExpired(true);

        if (!isOnAuthRoute && !isRedirecting) {
          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Adds Authorization header (if token exists) and returns response data
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return axiosInstance({
    ...config,
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
      ...options?.headers,
    },
  }).then(({ data }) => data as T);
};
